import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { motion } from "framer-motion";
const EFFECTS = [
    "shine",
    "firework",
    "magic",
    "love",
    "elegant",
    "meteor",
    "gold",
    "bubbles",
    "balloons",
];
/** Точное соответствие ключу из БД. */
const KEY_TO_EFFECT = {
    shine: "shine",
    firework: "firework",
    fireworks: "firework",
    magic: "magic",
    love: "love",
    elegant: "elegant",
    meteor: "meteor",
    starfall: "meteor",
    gold: "gold",
    bubbles: "bubbles",
    balloons: "balloons",
};
/** Подбирает видимый эффект под ключ (или null для пустого ключа). */
export function resolveEffect(key) {
    if (!key || !key.trim())
        return null;
    const k = key.toLowerCase().trim();
    if (KEY_TO_EFFECT[k])
        return KEY_TO_EFFECT[k];
    const has = (...subs) => subs.some((s) => k.includes(s));
    if (has("heart", "love", "lyub", "серд", "любов"))
        return "love";
    if (has("fire", "firework", "salut", "салют", "фейер"))
        return "firework";
    if (has("balloon", "shar", "шар"))
        return "balloons";
    if (has("bubble", "puzyr", "пузыр"))
        return "bubbles";
    if (has("meteor", "starfall", "звезд", "shoot"))
        return "meteor";
    if (has("gold", "zolot", "золот", "coin"))
        return "gold";
    if (has("elegant", "элегант", "pearl", "champagne"))
        return "elegant";
    if (has("magic", "волшеб", "witch"))
        return "magic";
    if (has("shine", "glow", "siyan", "сиян", "spark"))
        return "shine";
    let h = 0;
    for (let i = 0; i < k.length; i++)
        h = (h * 31 + k.charCodeAt(i)) >>> 0;
    return EFFECTS[h % EFFECTS.length];
}
export function isKnownAnimationKey(key) {
    return resolveEffect(key) !== null;
}
const STYLE = {
    shine: {
        count: 28,
        mode: "expand",
        colors: ["#fff7ed", "#fde68a", "#fbcfe8", "#fda4af", "#ffffff"],
        sizeMin: 6,
        sizeMax: 14,
        round: true,
        soft: true,
    },
    firework: {
        count: 48,
        mode: "burst",
        colors: ["#ff5544", "#ffb347", "#4ade80", "#38bdf8", "#c084fc", "#f472b6", "#facc15"],
        sizeMin: 4,
        sizeMax: 9,
    },
    magic: {
        count: 36,
        mode: "rise",
        char: "✦",
        colors: ["#c084fc", "#a78bfa", "#e879f9", "#f0abfc", "#818cf8"],
        sizeMin: 10,
        sizeMax: 18,
    },
    love: {
        count: 28,
        mode: "rise",
        char: "❤",
        colors: ["#fb7185", "#f43f5e", "#e11d48", "#fda4af"],
        sizeMin: 12,
        sizeMax: 22,
    },
    elegant: {
        count: 24,
        mode: "fall",
        colors: ["#faf7f2", "#e8dfd0", "#d4c4a8", "#ffffff"],
        sizeMin: 3,
        sizeMax: 7,
        soft: true,
    },
    meteor: {
        count: 18,
        mode: "diagonal",
        colors: ["#e0f2fe", "#bae6fd", "#7dd3fc", "#ffffff"],
        sizeMin: 3,
        sizeMax: 6,
        trail: true,
    },
    gold: {
        count: 32,
        mode: "fall",
        colors: ["#fbbf24", "#f59e0b", "#eab308", "#fde68a", "#d97706"],
        sizeMin: 8,
        sizeMax: 14,
        round: true,
    },
    bubbles: {
        count: 22,
        mode: "rise",
        colors: ["rgba(255,255,255,0.35)"],
        sizeMin: 10,
        sizeMax: 22,
        round: true,
    },
    balloons: {
        count: 14,
        mode: "rise",
        char: "🎈",
        colors: ["#ff5544"],
        sizeMin: 16,
        sizeMax: 26,
    },
};
function rand(min, max) {
    return min + Math.random() * (max - min);
}
function buildPieces(kind) {
    const style = STYLE[kind];
    const pieces = [];
    for (let i = 0; i < style.count; i++) {
        pieces.push({
            id: i,
            left: rand(5, 95),
            top: rand(20, 80),
            delay: rand(0, style.mode === "burst" ? 0.35 : 0.7),
            duration: rand(style.mode === "expand" || kind === "elegant" ? 2.2 : 1.3, style.mode === "diagonal" ? 1.6 : 3.0),
            drift: rand(-70, 70),
            rotate: rand(-240, 240),
            color: style.colors[i % style.colors.length],
            size: rand(style.sizeMin, style.sizeMax),
            angle: (360 / style.count) * i + rand(-12, 12),
            distance: rand(80, 180),
        });
    }
    return pieces;
}
function pieceMotion(piece, mode, loop) {
    const repeat = loop ? Infinity : 0;
    const repeatDelay = 0.45;
    switch (mode) {
        case "rise":
            return {
                initial: { top: "105%", left: `${piece.left}%`, opacity: 0, rotate: 0, x: 0 },
                animate: {
                    top: "-12%",
                    x: piece.drift,
                    opacity: [0, 1, 1, 0],
                    rotate: piece.rotate * 0.35,
                },
                transition: {
                    duration: piece.duration,
                    delay: piece.delay,
                    ease: "easeOut",
                    repeat,
                    repeatDelay,
                },
            };
        case "fall":
            return {
                initial: { top: "-8%", left: `${piece.left}%`, opacity: 0, rotate: 0, x: 0 },
                animate: {
                    top: "110%",
                    x: piece.drift,
                    opacity: [0, 1, 1, 0],
                    rotate: piece.rotate,
                },
                transition: {
                    duration: piece.duration,
                    delay: piece.delay,
                    ease: "easeIn",
                    repeat,
                    repeatDelay,
                },
            };
        case "diagonal":
            return {
                initial: {
                    top: `${piece.top * 0.5}%`,
                    left: `${55 + (piece.left % 45)}%`,
                    opacity: 0,
                    rotate: -35,
                    x: 0,
                    y: 0,
                },
                animate: {
                    x: -220 - piece.distance,
                    y: 180 + piece.distance * 0.6,
                    opacity: [0, 1, 1, 0],
                },
                transition: {
                    duration: piece.duration * 0.7,
                    delay: piece.delay,
                    ease: "linear",
                    repeat,
                    repeatDelay: 0.2,
                },
            };
        case "burst": {
            const rad = (piece.angle * Math.PI) / 180;
            const dx = Math.cos(rad) * piece.distance;
            const dy = Math.sin(rad) * piece.distance;
            return {
                initial: {
                    top: `${piece.top}%`,
                    left: `${piece.left}%`,
                    opacity: 0,
                    x: 0,
                    y: 0,
                    scale: 0.4,
                },
                animate: {
                    x: dx,
                    y: dy,
                    opacity: [0, 1, 1, 0],
                    scale: [0.4, 1.1, 0.8],
                },
                transition: {
                    duration: piece.duration * 0.75,
                    delay: piece.delay,
                    ease: "easeOut",
                    repeat,
                    repeatDelay: 0.6,
                },
            };
        }
        case "expand":
            return {
                initial: {
                    top: "50%",
                    left: "50%",
                    opacity: 0,
                    x: 0,
                    y: 0,
                    scale: 0.2,
                },
                animate: {
                    x: Math.cos((piece.angle * Math.PI) / 180) * piece.distance * 1.1,
                    y: Math.sin((piece.angle * Math.PI) / 180) * piece.distance * 0.85,
                    opacity: [0, 0.9, 0.5, 0],
                    scale: [0.2, 1.4, 1.8],
                },
                transition: {
                    duration: piece.duration,
                    delay: piece.delay * 0.5,
                    ease: "easeOut",
                    repeat,
                    repeatDelay: 0.5,
                },
            };
    }
}
/**
 * Оверлей открывающей анимации: частицы поверх сцены (pointer-events: none).
 * Пустой ключ → ничего. Каждый известный ключ — свой визуальный паттерн.
 */
export const OpenAnimationOverlay = ({ animationKey, play, loop = false, }) => {
    const kind = resolveEffect(animationKey);
    const pieces = React.useMemo(() => (kind ? buildPieces(kind) : []), [kind]);
    if (!kind || !play)
        return null;
    const style = STYLE[kind];
    return (_jsx("div", { "aria-hidden": "true", style: {
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 5,
        }, children: pieces.map((p) => {
            const motionProps = pieceMotion(p, style.mode, loop);
            const isBubble = kind === "bubbles";
            const isGold = kind === "gold";
            const isElegant = kind === "elegant";
            return (_jsx(motion.div, { initial: motionProps.initial, animate: motionProps.animate, transition: motionProps.transition, style: {
                    position: "absolute",
                    fontSize: style.char ? p.size + 6 : undefined,
                    color: style.char ? p.color : undefined,
                    width: style.char ? undefined : style.trail ? p.size * 8 : p.size,
                    height: style.char
                        ? undefined
                        : style.trail
                            ? p.size
                            : isElegant
                                ? p.size * 2.2
                                : isGold
                                    ? p.size * 0.7
                                    : p.size,
                    background: style.char
                        ? undefined
                        : isBubble
                            ? "rgba(255,255,255,0.28)"
                            : p.color,
                    border: isBubble ? "1px solid rgba(255,255,255,0.55)" : undefined,
                    borderRadius: style.round || isBubble ? "50%" : style.trail ? 999 : isElegant ? 1 : 2,
                    boxShadow: style.soft
                        ? `0 0 ${p.size}px ${p.color}`
                        : style.trail
                            ? `0 0 6px ${p.color}`
                            : isGold
                                ? `0 0 4px ${p.color}`
                                : undefined,
                    opacity: style.soft ? 0.85 : undefined,
                    transformOrigin: style.trail ? "right center" : undefined,
                }, children: style.char }, p.id));
        }) }));
};
